import { useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { Button } from '@material-ui/core';
import { isEmpty, merge } from 'lodash';

import AdminPage from '../AdminPage';
import DataTable from '../../data-table/DataTable';
import { useQueryLoader } from '../../data-table/Loaders';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import { Actions } from '../../page-framework';
import Link from '../../links/Link';
import DeleteDialog from '../../form/DeleteDialog';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import { useDataService } from '../../../services/ApiDataService';
import { AdminPages, buildPageId, buildRootPageTarget } from '../AdminLinkUtils';
import {
    VOIP_CALL_MANAGERS_USAGE_COUNT_API_URL,
    VOIP_CALL_MANAGERS_INITIAL_SORT,
    VOIP_CALL_MANAGERS_LIST_DATA_SOURCE,
    VOIP_CALL_MANAGERS_ROOT,
} from './VoipConstants';

const callManagerColumns = [
    {
        type: COLUMN_TYPES.LINK,
        title: t`Name`,
        mapping: 'name',
        width: 240,
        sortable: true,
        link: {
            target: `${VOIP_CALL_MANAGERS_ROOT}/:_id`,
        },
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Proxy`,
        mapping: 'sipProxy',
        width: 260,
        sortable: true,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Domain`,
        mapping: 'domain',
        width: 260,
        sortable: true,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`SIP Port`,
        mapping: 'sipPort',
        width: 120,
        sortable: true,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Description`,
        mapping: 'description',
        flex: 1,
        sortable: true,
    },
];

export default function VoipCallManagersListPage() {
    const loader = useQueryLoader(VOIP_CALL_MANAGERS_LIST_DATA_SOURCE, { disableFilterState: true });
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger();
    const dataService = useDataService();
    const formDeleteService = useFormDeleteService();
    const { deleteWrapper } = useFormDeleteWrapper();

    const [tableSelection, setTableSelection] = useState(undefined);
    const [tableState, setTableState] = useState(undefined);
    const [actionsDisabled, setActionsDisabled] = useState(true);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteDialogText, setDeleteDialogText] = useState(undefined);

    const handleSelectionChange = (selection) => {
        setTableSelection(selection);
        setActionsDisabled(!selection || (isEmpty(selection?.selection) && !selection?.inverted));
    };

    const getTableParams = () =>
        merge(
            {
                search: tableState?.search || '',
            },
            VOIP_CALL_MANAGERS_LIST_DATA_SOURCE.params
        );

    const handleRequestedDelete = async () => {
        const params = {
            ...getTableParams(),
            rowLimit: 100,
            sort: 'name',
        };
        try {
            const response = await dataService.post(VOIP_CALL_MANAGERS_USAGE_COUNT_API_URL, tableSelection, params);
            const count = Number(response?.count || 0);

            if (count > 0) {
                setDeleteDialogText(
                    t`The selected Call Manager(s) will be deleted. Along with the Call Managers, ${count} nPoint VoIP Configurations will be deleted as they use these Call Managers. Click Delete to continue.`
                );
            } else {
                setDeleteDialogText(undefined);
            }
        } catch (error) {
            // If the pre-check fails, keep default confirmation behavior.
            setDeleteDialogText(undefined);
        } finally {
            setShowDeleteDialog(true);
        }
    };

    const handleConfirmedDelete = () => {
        const params = getTableParams();

        return deleteWrapper({
            deleteFunction: () =>
                formDeleteService.deleteSelection(
                    `${VOIP_CALL_MANAGERS_LIST_DATA_SOURCE.url}/selection/remove`,
                    params,
                    tableSelection
                ),
            refresherFunc: refresher,
        })();
    };

    return (
        <AdminPage
            title={t`Call Managers`}
            pageId={buildPageId(AdminPages.VoipConfig)}
            breadcrumbs={[
                { link: buildRootPageTarget(AdminPages.VoipConfig), text: t`VoIP Configuration` },
                { text: t`Call Managers` },
            ]}
            actions={
                <Actions>
                    <Link to={`${buildRootPageTarget(AdminPages.VoipConfig)}/list`}>
                        <Button>{t`NPOINT Configuration`}</Button>
                    </Link>
                    <Button disabled={actionsDisabled} onClick={handleRequestedDelete}>
                        <Trans>Delete</Trans>
                    </Button>
                    <DeleteDialog
                        open={showDeleteDialog}
                        setOpen={setShowDeleteDialog}
                        onConfirm={handleConfirmedDelete}
                        plural={tableSelection?.selection?.length > 1}
                        confirmText={deleteDialogText}
                    />
                    <Button component={Link} to={`${VOIP_CALL_MANAGERS_ROOT}/new`} variant="contained" color="primary">
                        <Trans>Create</Trans>
                    </Button>
                </Actions>
            }
        >
            <DataTable
                tableId="callmanagers-list"
                loader={loader}
                triggerRef={triggerRef}
                columns={callManagerColumns}
                visibleRows={0}
                sorters={VOIP_CALL_MANAGERS_INITIAL_SORT}
                showSelectionColumn
                onStateChanged={setTableState}
                selectionOptions={{
                    mode: SELECTIONMODE_MULTI,
                    allowSelectAll: true,
                    idMapping: '_id',
                    onChange: handleSelectionChange,
                }}
            />
        </AdminPage>
    );
}
